import {createContext, ReactNode, use, useCallback, useRef, useState} from 'react';

import {noop, useOnMount} from 'shared/web';

import {useHeaderHeight} from '../../../hooks/useHeaderHeight';

const HeightContext = createContext(0);
const MeasureRefContext = createContext<(element: HTMLDivElement | null) => void>(noop);

/**
 * Shares the height of the saved-filters panel with pages that size themselves to
 * the viewport (StatisticsPage keeps its chart card exactly one screen tall and
 * must subtract the panel). Only the part of the panel still visible below the
 * sticky header counts — as the page scrolls and the panel slides away, the
 * reported height shrinks towards 0 and the chart grows to reclaim the space.
 */
function SavedFiltersPanelHeightProvider(props: {children: ReactNode}) {
  const headerHeight = useHeaderHeight();
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const headerHeightRef = useRef(headerHeight);
  headerHeightRef.current = headerHeight;

  const updateHeight = useCallback(() => {
    const element = elementRef.current;
    if (!element) {
      setHeight(0);
      return;
    }
    const rect = element.getBoundingClientRect();
    const visibleHeight = Math.max(
      0,
      Math.min(element.offsetHeight, rect.bottom - headerHeightRef.current)
    );
    // Rounded so fractional scroll positions don't trigger endless re-renders
    setHeight(Math.round(visibleHeight));
  }, []);

  // Callback ref: observes the panel wrapper when mounted, reports 0 when it
  // unmounts (flag off, mobile, error state).
  const measureRef = useCallback(
    (element: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      elementRef.current = element;
      if (element) {
        const observer = new ResizeObserver(updateHeight);
        observer.observe(element);
        observerRef.current = observer;
      }
      updateHeight();
    },
    [updateHeight]
  );

  // updateHeight is referentially stable (empty useCallback deps), so this is a mount effect
  useOnMount(() => {
    window.addEventListener('scroll', updateHeight, {passive: true});
    window.addEventListener('resize', updateHeight);
    return () => {
      window.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  });

  return (
    <MeasureRefContext.Provider value={measureRef}>
      <HeightContext.Provider value={height}>{props.children}</HeightContext.Provider>
    </MeasureRefContext.Provider>
  );
}

const useSavedFiltersPanelHeight = () => use(HeightContext);
const useSavedFiltersPanelMeasureRef = () => use(MeasureRefContext);

export {
  SavedFiltersPanelHeightProvider,
  useSavedFiltersPanelHeight,
  useSavedFiltersPanelMeasureRef,
};
